import React, { useState, useEffect } from 'react';
import { WRAP_TEXT } from '../shared/wrapText';
import ModalLayer from './ModalLayer';
import { supabase } from '../supabaseClient';
import { useTheme } from '../ThemeContext';
import { agentAuthHeaders } from '../utils/functionAuth';
import { getApiUrl, isNativeApp, NATIVE_PLAN_NOTICE } from '../utils/platform';
import { seatsOpen, teamFullMessage } from '../shared/teamSeats';
import {
  addonRemovalLine,
  agentsRemovedBySolo,
  emptySeatCopy,
  emptySeatCount,
  nextLowerSeatCount,
  seatFloorMessage,
  seatReductionMessage,
  soloGraceLine,
  teamConfirmName,
  teamNameMatches,
} from '../shared/planSwitchUi';
import { addonQuote, HOMEOWNER_TEAM_SOLO_GRACE_DAYS, seatMonthlyTotal } from '../shared/planLimits';
import { addonKeptOnSubscriptionChange, trialConsentApplies } from '../shared/consentCopy';
import TrialConsentNotice, { trialButtonFor } from './TrialConsentNotice';
import { showToast } from './Toast';
import ConfirmDialog from './ConfirmDialog';
import { STRIPE_PRICES, TEAM_PRICING, updateSubscription } from '../utils/stripe';

const TeamManagement = ({ profile }) => {
  const { colors, isDark } = useTheme();
  const [team, setTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    firstName: '',
    lastName: ''
  });
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    market: ''
  });
  const [notice, setNotice] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [reducingSeats, setReducingSeats] = useState(false);
  const [seatConfirm, setSeatConfirm] = useState(null);
  const [soloOpen, setSoloOpen] = useState(false);
  const [soloTyped, setSoloTyped] = useState('');
  const [switchingSolo, setSwitchingSolo] = useState(false);
  const [trialAgreed, setTrialAgreed] = useState(false);
  const trialing = trialConsentApplies({ profile, checkout: false });
  const trialConsent = trialing ? { autoRenewal: true, kind: 'trial' } : null;

  const brandColor = '#59c4c4';

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (profile?.team_id) {
      loadTeamData();
    } else {
      setLoading(false);
    }
  }, [profile]);

  const loadTeamData = async () => {
    try {
      const response = await fetch(getApiUrl('/.netlify/functions/manage-team'), {
        method: 'POST',
        headers: await agentAuthHeaders(),
        body: JSON.stringify({ action: 'list' }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error || 'Could not load your team.');
      }
      setTeam(result.team || null);
      setTeamMembers(result.members || []);
      setInvites(result.invites || []);
      setNotice('');
    } catch (error) {
      console.error('Error loading team data:', error);
      setNotice(error.message || 'Could not load your team.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteForm.email.trim()) {
      setNotice('Enter an email address.');
      return;
    }
    if (seatsOpen(team, invites.length) <= 0) {
      setNotice(`Team is full (${team?.max_seats || 0} seats). Remove a member or cancel an invite before adding more.`);
      return;
    }

    setSendingInvite(true);
    setNotice('');
    try {
      const response = await fetch(getApiUrl('/.netlify/functions/manage-team'), {
        method: 'POST',
        headers: await agentAuthHeaders(),
        body: JSON.stringify({
          action: 'invite',
          email: inviteForm.email.trim(),
          firstName: inviteForm.firstName.trim(),
          lastName: inviteForm.lastName.trim(),
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Could not send the invite.');

      const code = result.invite && result.invite.invite_code;
      setNotice(result.emailSent
        ? `Invite sent to ${inviteForm.email.trim()}.${code ? ` Invite code: ${code}` : ''}`
        : `Invite saved for ${inviteForm.email.trim()}, but the email could not be sent. Use Resend.`);
      setShowInviteModal(false);
      setInviteForm({ email: '', firstName: '', lastName: '' });
      loadTeamData();
    } catch (error) {
      console.error('Error creating invite:', error);
      setNotice(error.message || 'Could not send the invite.');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleResendInvite = async (inviteId) => {
    setNotice('');
    try {
      const response = await fetch(getApiUrl('/.netlify/functions/manage-team'), {
        method: 'POST',
        headers: await agentAuthHeaders(),
        body: JSON.stringify({ action: 'resend', inviteId }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Could not resend the invite.');
      setNotice('Invite email sent again.');
    } catch (error) {
      setNotice(error.message || 'Could not resend the invite.');
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (memberId === profile.id) {
      showToast('You cannot remove yourself as the team owner.');
      return;
    }

    if (!window.confirm(`Remove ${memberName} from the team?\n\nAll of their clients will be transferred to you (team owner) and their branding will be updated to yours.`)) {
      return;
    }

    try {
      // Transfer all of the member's clients to the team owner
      const { error: clientsError } = await supabase
        .from('clients')
        .update({
          agent_id: profile.id,
          assigned_agent_id: profile.id
        })
        .eq('agent_id', memberId);

      if (clientsError) {
        console.error('Error transferring clients:', clientsError);
        showToast('Error transferring clients. Please try again.');
        return;
      }

      // Log activity
      await supabase.from('team_activity_log').insert({
        team_id: profile.team_id,
        agent_id: profile.id,
        action_type: 'member_removed',
        description: `${memberName} was removed from the team, clients transferred to team owner`
      });

      const response = await fetch(getApiUrl('/.netlify/functions/profile-entitlements'), {
        method: 'POST',
        headers: await agentAuthHeaders(),
        body: JSON.stringify({ action: 'remove-member', memberId }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error || 'Update failed');
      }

      showToast(`${memberName} has been removed from the team.\n\nAll clients have been transferred to you.`);
      loadTeamData();
    } catch (error) {
      console.error('Error removing member:', error);
      showToast('Error removing member. Please try again.');
    }
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setEditForm({
      firstName: member.first_name || '',
      lastName: member.last_name || '',
      email: member.email || '',
      phone: member.phone || '',
      market: member.market || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.firstName || !editForm.lastName || !editForm.email) {
      showToast('Please fill in all required fields (First Name, Last Name, Email)');
      return;
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: editForm.firstName,
          last_name: editForm.lastName,
          email: editForm.email,
          phone: editForm.phone || '',
          market: editForm.market || ''
        })
        .eq('id', editingMember.id);

      if (error) throw error;

      // Log activity
      await supabase.from('team_activity_log').insert({
        team_id: profile.team_id,
        agent_id: profile.id,
        action_type: 'team_updated',
        description: `Updated ${editForm.firstName} ${editForm.lastName}'s profile information`
      });

      showToast('Team member updated successfully!');
      setShowEditModal(false);
      setEditingMember(null);
      loadTeamData();
    } catch (error) {
      console.error('Error updating member:', error);
      showToast('Error updating team member. Please try again.');
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      const response = await fetch(getApiUrl('/.netlify/functions/manage-team'), {
        method: 'POST',
        headers: await agentAuthHeaders(),
        body: JSON.stringify({ action: 'cancel', inviteId }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Could not cancel the invite.');
      setNotice('Invite cancelled.');
      loadTeamData();
    } catch (error) {
      console.error('Error cancelling invite:', error);
      setNotice(error.message || 'Could not cancel the invite.');
    }
  };

  const requestSeatReduction = () => {
    const next = nextLowerSeatCount(team, invites.length, teamMembers.length);
    const fromAgents = Number(team && team.max_seats);
    const fromPlan = TEAM_PRICING.find((plan) => plan.agents === fromAgents);
    const toPlan = TEAM_PRICING.find((plan) => plan.agents === next);
    if (!next || !toPlan) {
      showToast('That seat count is not available.');
      return;
    }
    const addon = team && team.homeowner_addon;
    const fromAddon = addonQuote({ addon, seats: fromAgents, shared: true });
    setTrialAgreed(false);
    setSeatConfirm({
      fromAgents,
      toAgents: next,
      fromPrice: fromPlan ? seatMonthlyTotal(fromPlan.price, fromAgents, addon) : null,
      toPrice: seatMonthlyTotal(toPlan.price, next, addon),
      addonLabel: fromAddon.addon === 'none' ? '' : fromAddon.label,
      priceId: toPlan.priceId,
    });
  };

  const confirmSeatReduction = async () => {
    if (!seatConfirm || !seatConfirm.priceId) {
      showToast('That seat count is not available.');
      return;
    }
    if (!profile.stripe_customer_id) {
      showToast('Add a payment method before changing seats.');
      return;
    }
    if (trialing && !trialAgreed) return;
    setReducingSeats(true);
    try {
      await updateSubscription({
        customerId: profile.stripe_customer_id,
        newPriceId: seatConfirm.priceId,
        agentId: profile.id,
        ...(trialConsent ? { consent: trialConsent } : {}),
      });
      showToast('Seats updated.');
      window.location.reload();
    } catch (error) {
      console.error('Error reducing seats:', error);
      showToast(error.message || 'Could not reduce seats.');
    } finally {
      setReducingSeats(false);
    }
  };

  const confirmSoloSwitch = async () => {
    const expectedName = teamConfirmName(team && team.name);
    if (!teamNameMatches(expectedName, soloTyped)) return;
    if (!profile.stripe_customer_id) {
      showToast('Add a payment method before changing plans.');
      return;
    }
    if (!STRIPE_PRICES.SOLO) {
      showToast('The Solo Agent plan is not available.');
      return;
    }
    if (trialing && !trialAgreed) return;
    setSwitchingSolo(true);
    try {
      await updateSubscription({
        customerId: profile.stripe_customer_id,
        newPriceId: STRIPE_PRICES.SOLO,
        agentId: profile.id,
        confirmSolo: true,
        ...(trialConsent ? { consent: trialConsent } : {}),
      });
      showToast('Switched to Solo.');
      window.location.reload();
    } catch (error) {
      console.error('Error switching to Solo:', error);
      showToast(error.message || 'Could not switch to Solo.');
      setSwitchingSolo(false);
    }
  };

  const handleLeaveTeam = async () => {
    setLeaveOpen(false);

    try {
      const teamId = profile.team_id;

      // Log activity
      await supabase.from('team_activity_log').insert({
        team_id: teamId,
        agent_id: profile.id,
        action_type: 'member_left',
        description: `${profile.first_name} ${profile.last_name} left the team`
      });

      const response = await fetch(getApiUrl('/.netlify/functions/profile-entitlements'), {
        method: 'POST',
        headers: await agentAuthHeaders(),
        body: JSON.stringify({ action: 'leave-team' }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error || 'Update failed');
      }

      showToast('You have left the team. Refreshing...');
      window.location.reload();
    } catch (error) {
      console.error('Error leaving team:', error);
      showToast('Error leaving team. Please try again.');
    }
  };

  const handleUpdateTeamName = async () => {
    const newName = prompt('Enter new team name:', team.name);
    if (!newName || newName === team.name) return;

    try {
      const { error } = await supabase
        .from('teams')
        .update({ name: newName, updated_at: new Date().toISOString() })
        .eq('id', profile.team_id);

      if (error) throw error;

      // Log activity
      await supabase.from('team_activity_log').insert({
        team_id: profile.team_id,
        agent_id: profile.id,
        action_type: 'team_updated',
        description: `Team name changed to "${newName}"`
      });

      showToast('Team name updated!');
      loadTeamData();
    } catch (error) {
      console.error('Error updating team name:', error);
      showToast('Error updating team name.');
    }
  };

  if (!profile?.team_id) {
    return (
      <div style={styles.container}>
        <h2 data-tour="team" style={{...styles.title, color: colors.text}}>Team</h2>
        <div style={{...styles.card, background: colors.surface, border: `1px solid ${colors.border}`}}>
          <p style={{color: colors.textSecondary}}>
          {profile?.subscription_plan === 'team' || profile?.is_team_owner
            ? 'Your team is not ready yet. Reload after checkout finishes, then you can add members here.'
            : 'You are not part of a team. To join one, enter the invite code in Settings.'}
        </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <h2 data-tour="team" style={{...styles.title, color: colors.text}}>Team</h2>
        <p style={{color: colors.textSecondary}}>Loading...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div className="team-page-header">
        <h2 style={{...styles.title, color: colors.text, marginBottom: 0}}>Team</h2>
        {profile?.is_team_owner && (
          <button
            type="button"
            data-tour="team"
            onClick={() => setShowInviteModal(true)}
            style={{
              ...styles.btnPrimary,
              background: `linear-gradient(135deg, ${brandColor} 0%, #4ab3b3 100%)`
            }}
            disabled={seatsOpen(team, invites.length) <= 0}
          >
            Add team member
          </button>
        )}
      </div>

      {/* Team Info Card */}
      <div style={{...styles.card, background: colors.surface, border: `1px solid ${colors.border}`, marginBottom: '1.5rem'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '1rem', minWidth: 0, flexWrap: 'wrap'}}>
          <h3 style={{...styles.cardTitle, color: colors.text, margin: 0, flex: '1 1 12rem', ...WRAP_TEXT}}>
            {team?.name || 'Your Team'}
          </h3>
          <div style={{display: 'flex', gap: '0.5rem', flex: '0 0 auto'}}>
            {profile.is_team_owner && (
              <button
                onClick={handleUpdateTeamName}
                style={{...styles.btnSecondary, fontSize: '0.85rem', padding: '0.4rem 0.8rem'}}
              >
                Rename
              </button>
            )}
            {!profile.is_team_owner && profile.role !== 'owner' && (
              <button
                onClick={() => setLeaveOpen(true)}
                style={{...styles.btnSecondary, fontSize: '0.85rem', padding: '0.4rem 0.8rem', background: '#ef4444', color: 'white', border: 'none'}}
              >
                Leave Team
              </button>
            )}
          </div>
        </div>
        {!profile.is_team_owner && profile.role !== 'owner' && (
          <p style={{margin: '0 0 1rem', color: colors.textSecondary}}>
            You are currently a member of a team. Your team owner manages the subscription and billing.
          </p>
        )}
        {profile.is_team_owner && (
          <>
            <div style={styles.statRow}>
              <div>
                <div style={{fontSize: '2rem', fontWeight: 'bold', color: brandColor}}>
                  {team?.current_seats || 0}/{team?.max_seats || 5}
                </div>
                <div style={{fontSize: '0.9rem', color: colors.textSecondary}}>Seats Used</div>
              </div>
              {profile.subscription_plan && (
                <div>
                  <div style={{fontSize: '1.2rem', fontWeight: '500', color: colors.text}}>
                    {profile.subscription_plan.charAt(0).toUpperCase() + profile.subscription_plan.slice(1)} Plan
                  </div>
                  <div style={{fontSize: '0.9rem', color: colors.textSecondary}}>Current Plan</div>
                </div>
              )}
            </div>
            {seatsOpen(team, invites.length) <= 0 && (
              <p style={{marginTop: '1rem', padding: '0.75rem', background: '#fef3c7', borderRadius: '8px', color: '#92400e'}}>
                {teamFullMessage(team, invites.length)}
              </p>
            )}
            <SeatStepper
              team={team}
              pendingCount={invites.length}
              memberCount={teamMembers.length}
              colors={colors}
              reducing={reducingSeats}
              onReduce={requestSeatReduction}
            />
            {notice && (
              <p style={{marginTop: '1rem', color: colors.text}}>{notice}</p>
            )}
          </>
        )}
      </div>

      {/* Team Members */}
      <div style={{...styles.card, background: colors.surface, border: `1px solid ${colors.border}`, marginBottom: '1.5rem'}}>
        <h3 style={{...styles.cardTitle, color: colors.text}}>Team Members ({teamMembers.length})</h3>
        {teamMembers.length === 0 ? (
          <p style={{color: colors.textSecondary}}>No team members yet.</p>
        ) : (
          <div style={styles.list}>
            {teamMembers.map((member) => (
              <div key={member.id} className="team-member-row" style={{ borderBottom: `1px solid ${colors.border}` }}>
                <div className="team-row-details">
                  <div style={{fontWeight: 500, color: colors.text, ...WRAP_TEXT}}>
                    {member.first_name} {member.last_name}
                    {member.id === profile.id && <span style={{marginLeft: '0.5rem', fontSize: '0.85rem', color: brandColor}}>(You)</span>}
                  </div>
                  <div style={{fontSize: '0.9rem', color: colors.textSecondary, ...WRAP_TEXT}}>{member.email}</div>
                  <div style={{fontSize: '0.85rem', color: colors.textSecondary, marginTop: '0.25rem'}}>
                    {member.is_team_owner ? 'Team Owner' : 'Team Member'}
                  </div>
                </div>
                {profile.is_team_owner && member.id !== profile.id && (
                  <div className="team-member-actions">
                    <button
                      onClick={() => handleEditMember(member)}
                      style={{...styles.btnSecondary, color: colors.text, border: `1px solid ${colors.border}`}}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleRemoveMember(member.id, `${member.first_name} ${member.last_name}`)}
                      style={{...styles.btnSecondary, background: '#ef4444', color: 'white', border: 'none'}}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {profile.is_team_owner && (
        <div style={{...styles.card, background: colors.surface, border: `1px solid ${colors.border}`}}>
          <h3 style={{...styles.cardTitle, color: colors.text}}>Pending invites ({invites.length})</h3>
          <p style={{color: colors.textSecondary, marginTop: 0}}>
            Each person gets an email with a join link. The link signs them up, or adds them if they already have an account. The invite code below is a backup.
          </p>
          {invites.length === 0 ? (
            <p style={{color: colors.textSecondary}}>No pending invites.</p>
          ) : (
            <div style={styles.list}>
              {invites.map((invite) => {
                const inviteName = [invite.first_name, invite.last_name]
                  .map((part) => String(part || '').trim())
                  .filter(Boolean)
                  .join(' ');
                const inviteEmail = String(invite.email || '').trim();
                const showName = inviteName && inviteName.toLowerCase() !== inviteEmail.toLowerCase();
                return (
                <div key={invite.id} className="team-invite-row" style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <div className="team-row-details">
                    <div style={{fontWeight: 500, color: colors.text, ...WRAP_TEXT}}>
                      {showName ? inviteName : inviteEmail}
                    </div>
                    {showName && (
                      <div style={{fontSize: '0.9rem', color: colors.textSecondary, ...WRAP_TEXT}}>{inviteEmail}</div>
                    )}
                    <div style={{fontSize: '0.85rem', color: brandColor, marginTop: '0.25rem', fontFamily: 'monospace', ...WRAP_TEXT}}>
                      Code: {invite.invite_code}
                    </div>
                    {invite.expires_at && (
                      <div style={{fontSize: '0.8rem', color: colors.textSecondary, marginTop: '0.25rem'}}>
                        Expires: {new Date(invite.expires_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  <div className="team-invite-actions">
                    <button
                      type="button"
                      onClick={() => handleResendInvite(invite.id)}
                      style={{...styles.btnSecondary, color: colors.text, border: `1px solid ${colors.border}`}}
                    >
                      Resend
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCancelInvite(invite.id)}
                      style={{...styles.btnSecondary, color: colors.text, border: `1px solid ${colors.border}`}}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {profile.is_team_owner && (
        <SoloDangerZone
          colors={colors}
          isDark={isDark}
          onSwitch={() => {
            setSoloTyped('');
            setTrialAgreed(false);
            setSoloOpen(true);
          }}
        />
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <ModalLayer style={styles.modalOverlay}>
          <div className="app-modal-card" style={{...styles.modal, background: colors.surface, border: `1px solid ${colors.border}`}}>
            <div className="app-modal-header" style={{ background: colors.surface }}>
            <h3 style={{...styles.cardTitle, color: colors.text, margin: 0}}>Add team member</h3>
            </div>
            <p style={{color: colors.textSecondary, marginTop: 0}}>They'll get an email from Pacarion with a join link.</p>
            <div style={styles.form}>
              <input
                type="email"
                placeholder="Email"
                aria-label="Email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                style={{...styles.input, background: colors.background, color: colors.text, border: `1px solid ${colors.border}`}}
              />
              <input
                type="text"
                placeholder="First name (optional)"
                aria-label="First name"
                value={inviteForm.firstName}
                onChange={(e) => setInviteForm({...inviteForm, firstName: e.target.value})}
                style={{...styles.input, background: colors.background, color: colors.text, border: `1px solid ${colors.border}`}}
              />
              <input
                type="text"
                placeholder="Last name (optional)"
                aria-label="Last name"
                value={inviteForm.lastName}
                onChange={(e) => setInviteForm({...inviteForm, lastName: e.target.value})}
                style={{...styles.input, background: colors.background, color: colors.text, border: `1px solid ${colors.border}`}}
              />
              <div style={{display: 'flex', gap: '0.75rem', marginTop: '1rem'}}>
                <button
                  onClick={handleSendInvite}
                  disabled={sendingInvite}
                  style={{...styles.btnPrimary, background: `linear-gradient(135deg, ${brandColor} 0%, #4ab3b3 100%)`}}
                >
                  {sendingInvite ? 'Sending…' : 'Send invite'}
                </button>
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteForm({ email: '', firstName: '', lastName: '' });
                  }}
                  style={{...styles.btnSecondary, color: colors.text, border: `1px solid ${colors.border}`}}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </ModalLayer>
      )}

      {/* Edit Member Modal */}
      {showEditModal && (
        <ModalLayer style={styles.modalOverlay}>
          <div className="app-modal-card" style={{...styles.modal, background: colors.surface, border: `1px solid ${colors.border}`}}>
            <div className="app-modal-header" style={{ background: colors.surface }}>
            <h3 style={{...styles.cardTitle, color: colors.text, margin: 0}}>Edit Team Member</h3>
            </div>
            <div style={styles.form}>
              <input
                type="text"
                placeholder="First Name *"
                value={editForm.firstName}
                onChange={(e) => setEditForm({...editForm, firstName: e.target.value})}
                style={{...styles.input, background: colors.background, color: colors.text, border: `1px solid ${colors.border}`}}
              />
              <input
                type="text"
                placeholder="Last Name *"
                value={editForm.lastName}
                onChange={(e) => setEditForm({...editForm, lastName: e.target.value})}
                style={{...styles.input, background: colors.background, color: colors.text, border: `1px solid ${colors.border}`}}
              />
              <input
                type="email"
                placeholder="Email *"
                value={editForm.email}
                onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                style={{...styles.input, background: colors.background, color: colors.text, border: `1px solid ${colors.border}`}}
              />
              <input
                type="text"
                placeholder="Phone"
                value={editForm.phone}
                onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                style={{...styles.input, background: colors.background, color: colors.text, border: `1px solid ${colors.border}`}}
              />
              <input
                type="text"
                placeholder="Market"
                value={editForm.market}
                onChange={(e) => setEditForm({...editForm, market: e.target.value})}
                style={{...styles.input, background: colors.background, color: colors.text, border: `1px solid ${colors.border}`}}
              />
              <div style={{display: 'flex', gap: '0.75rem', marginTop: '1rem'}}>
                <button
                  onClick={handleSaveEdit}
                  style={{...styles.btnPrimary, background: `linear-gradient(135deg, ${brandColor} 0%, #4ab3b3 100%)`}}
                >
                  Save Changes
                </button>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingMember(null);
                  }}
                  style={{...styles.btnSecondary, color: colors.text, border: `1px solid ${colors.border}`}}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </ModalLayer>
      )}
      <ConfirmDialog
        open={leaveOpen}
        title="Leave Team"
        message="Are you sure you want to leave this team? Your personal clients and vendors will remain yours."
        confirmLabel="Leave Team"
        onCancel={() => setLeaveOpen(false)}
        onConfirm={handleLeaveTeam}
      />
      <ConfirmDialog
        open={Boolean(seatConfirm)}
        title={seatConfirm ? `Reduce to ${seatConfirm.toAgents} seats?` : 'Reduce seats?'}
        message={seatConfirm ? seatReductionMessage(seatConfirm) : ''}
        confirmLabel={trialing && seatConfirm
          ? trialButtonFor({
            planName: 'team',
            agents: seatConfirm.toAgents,
            addon: addonKeptOnSubscriptionChange('team', team && team.homeowner_addon),
            profile,
          })
          : 'Reduce seats'}
        confirmDisabled={trialing && !trialAgreed}
        busy={reducingSeats}
        onCancel={() => {
          if (!reducingSeats) setSeatConfirm(null);
        }}
        onConfirm={confirmSeatReduction}
      >
        {trialing && seatConfirm ? (
          <div>
            <p style={{ margin: '0 0 1rem', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
              {seatReductionMessage(seatConfirm)}
            </p>
            <TrialConsentNotice
              planName="team"
              agents={seatConfirm.toAgents}
              addon={addonKeptOnSubscriptionChange('team', team && team.homeowner_addon)}
              profile={profile}
              checked={trialAgreed}
              onChange={setTrialAgreed}
              colors={colors}
            />
          </div>
        ) : null}
      </ConfirmDialog>
      {soloOpen && (
        <SoloSwitchDialog
          teamName={teamConfirmName(team && team.name)}
          typed={soloTyped}
          onTyped={setSoloTyped}
          members={agentsRemovedBySolo(teamMembers, profile.id)}
          invites={invites}
          addonLine={addonRemovalLine(team && team.homeowner_addon)}
          graceLine={soloGraceLine(HOMEOWNER_TEAM_SOLO_GRACE_DAYS)}
          notice={trialing ? (
            <TrialConsentNotice
              planName="solo"
              agents={1}
              addon={addonKeptOnSubscriptionChange('solo', team && team.homeowner_addon)}
              profile={profile}
              checked={trialAgreed}
              onChange={setTrialAgreed}
              colors={colors}
            />
          ) : null}
          confirmLabel={trialing
            ? trialButtonFor({
              planName: 'solo',
              agents: 1,
              addon: addonKeptOnSubscriptionChange('solo', team && team.homeowner_addon),
              profile,
            })
            : 'Switch to Solo'}
          agreementMet={!trialing || trialAgreed}
          busy={switchingSolo}
          colors={colors}
          onCancel={() => {
            if (!switchingSolo) setSoloOpen(false);
          }}
          onConfirm={confirmSoloSwitch}
        />
      )}
    </div>
  );
};

function SeatStepper({ team, pendingCount, memberCount, colors, reducing, onReduce }) {
  const next = nextLowerSeatCount(team, pendingCount, memberCount);
  const max = Number(team && team.max_seats);
  const current = TEAM_PRICING.find((plan) => plan.agents === max);
  const monthly = current ? seatMonthlyTotal(current.price, max, team && team.homeowner_addon) : null;
  const empty = emptySeatCount(team, pendingCount);
  const blocked = seatFloorMessage(team, pendingCount, memberCount);
  const native = isNativeApp();
  const unavailable = reducing || next == null;
  return (
    <div style={{ marginTop: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 650, color: colors.text, ...WRAP_TEXT }}>
            {Number.isFinite(max) ? max : 0} seats
            {monthly != null ? ` · $${monthly}/month` : ''}
          </div>
          {empty > 0 && (
            <div style={{ color: colors.textSecondary, fontSize: '0.9rem', marginTop: '0.2rem' }}>
              {emptySeatCopy(empty)}
            </div>
          )}
        </div>
        {native ? (
          <span style={{ color: colors.textSecondary, fontSize: '0.9rem' }}>{NATIVE_PLAN_NOTICE}</span>
        ) : (
          <button
            type="button"
            aria-label="Reduce one seat"
            onClick={onReduce}
            disabled={unavailable}
            style={{
              ...styles.btnSecondary,
              minHeight: '44px',
              padding: '0.55rem 0.9rem',
              color: colors.text,
              border: `1px solid ${colors.border}`,
              opacity: unavailable ? 0.5 : 1,
              cursor: unavailable ? 'not-allowed' : 'pointer',
            }}
          >
            {reducing ? 'Reducing...' : '− Reduce one seat'}
          </button>
        )}
      </div>
      {!native && next == null && blocked ? (
        <p style={{ margin: '0.75rem 0 0', color: colors.textSecondary, fontSize: '0.9rem' }}>{blocked}</p>
      ) : null}
    </div>
  );
}

function SoloDangerZone({ colors, isDark, onSwitch }) {
  if (isNativeApp()) return null;
  const danger = isDark ? '#fecaca' : '#991b1b';
  return (
    <section
      aria-labelledby="team-danger-zone"
      style={{
        ...styles.card,
        background: colors.surface,
        border: `2px solid ${danger}`,
        marginTop: '1.5rem',
      }}
    >
      <h3 id="team-danger-zone" style={{ ...styles.cardTitle, color: danger, marginTop: 0 }}>
        Danger zone
      </h3>
      <p style={{ marginTop: 0, color: colors.text }}>
        Switch to Solo removes every other agent and the homeowner add-on. Reducing seats does not do this.
      </p>
      <button
        type="button"
        onClick={onSwitch}
        style={{
          minHeight: '44px',
          padding: '0.65rem 1rem',
          borderRadius: '8px',
          border: `2px solid ${danger}`,
          background: 'transparent',
          color: danger,
          fontWeight: 650,
          cursor: 'pointer',
        }}
      >
        Switch to Solo
      </button>
    </section>
  );
}

function inviteLabel(invite) {
  const name = [invite.first_name, invite.last_name]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' ');
  const email = String(invite.email || '').trim();
  if (name && email) return `${name} (${email})`;
  return name || email || 'Pending invite';
}

function SoloSwitchDialog({
  teamName,
  typed,
  onTyped,
  members,
  invites,
  addonLine,
  graceLine,
  notice = null,
  confirmLabel = 'Switch to Solo',
  agreementMet = true,
  busy,
  colors,
  onCancel,
  onConfirm,
}) {
  const matches = teamNameMatches(teamName, typed);
  const canConfirm = matches && agreementMet && !busy;
  return (
    <ModalLayer style={styles.modalOverlay}>
      <div
        className="app-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="solo-switch-title"
        style={{
          ...styles.modal,
          background: colors.surface,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          maxHeight: '90vh',
          overflowY: 'auto',
          boxSizing: 'border-box',
        }}
      >
        <div className="app-modal-header" style={{ background: colors.surface }}>
          <h3 id="solo-switch-title" style={{ ...styles.cardTitle, color: colors.text, margin: 0 }}>
            Switch to Solo
          </h3>
        </div>
        <p style={{ marginTop: '1rem', color: colors.text }}>
          This leaves the team plan. It is separate from reducing seats.
        </p>
        {members.length === 0 ? (
          <p style={{ color: colors.text }}>No other agents will be removed.</p>
        ) : (
          <>
            <p style={{ marginBottom: '0.35rem', color: colors.text }}>These agents will be removed:</p>
            <ul style={{ marginTop: 0, paddingLeft: '1.2rem', color: colors.text }}>
              {members.map((member) => (
                <li key={member.id}>{member.label}</li>
              ))}
            </ul>
          </>
        )}
        <p style={{ color: colors.text }}>{addonLine}</p>
        <p style={{ color: colors.text }}>{graceLine}</p>
        {notice}
        {invites.length > 0 && (
          <>
            <p style={{ marginBottom: '0.35rem', color: colors.text }}>These pending invites will be cancelled:</p>
            <ul style={{ marginTop: 0, paddingLeft: '1.2rem', color: colors.text }}>
              {invites.map((invite) => (
                <li key={invite.id}>{inviteLabel(invite)}</li>
              ))}
            </ul>
          </>
        )}
        <div className="app-modal-actions" style={{ background: colors.surface, flexDirection: 'column', alignItems: 'stretch' }}>
          <label htmlFor="solo-team-name" style={{ display: 'block', fontWeight: 650, color: colors.text, marginBottom: '0.4rem' }}>
            {`Type ${teamName} to continue`}
          </label>
          <input
            id="solo-team-name"
            value={typed}
            autoComplete="off"
            onChange={(event) => onTyped(event.target.value)}
            style={{
              ...styles.input,
              width: '100%',
              boxSizing: 'border-box',
              background: colors.background,
              color: colors.text,
              border: `1px solid ${colors.border}`,
            }}
          />
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              style={{ ...styles.btnSecondary, minHeight: '44px', color: colors.text, border: `1px solid ${colors.border}` }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={!canConfirm}
              style={{
                minHeight: '44px',
                padding: '0.65rem 1rem',
                borderRadius: '8px',
                border: 'none',
                background: '#ef4444',
                color: 'white',
                fontWeight: 650,
                cursor: canConfirm ? 'pointer' : 'not-allowed',
                opacity: canConfirm ? 1 : 0.5,
              }}
            >
              {busy ? 'Switching...' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </ModalLayer>
  );
}

const styles = {
  container: {
    padding: '1rem',
    minWidth: 0,
    maxWidth: '100%'
  },
  title: {
    fontSize: '1.8rem',
    marginBottom: '1rem'
  },
  card: {
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box'
  },
  cardTitle: {
    fontSize: '1.2rem',
    marginBottom: '1rem'
  },
  statRow: {
    display: 'flex',
    gap: '2rem'
  },
  list: {
    display: 'flex',
    flexDirection: 'column'
  },
  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 0',
    gap: '1rem'
  },
  btnPrimary: {
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    border: 'none',
    color: 'white',
    fontWeight: 500,
    cursor: 'pointer',
    fontSize: '0.95rem'
  },
  btnSecondary: {
    padding: '0.5rem 1rem',
    borderRadius: '8px',
    background: 'transparent',
    fontWeight: 500,
    cursor: 'pointer',
    fontSize: '0.9rem'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    borderRadius: '12px',
    padding: '2rem',
    maxWidth: '500px',
    width: '90%',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  input: {
    padding: '0.75rem',
    borderRadius: '8px',
    fontSize: '1rem'
  }
};

export default TeamManagement;
